import { useId, useLayoutEffect, useRef, useState } from "react";
import { getPodColor } from "./podColors";
import { formatTime } from "./dateUtils";
import { repeatLabel, weekdayOf } from "./repeat";

// Shared Add / Edit event form (Glow redesign, 2026-10-10). Presentational only: Calendar.js owns the
// values and does all saving (addEvent / saveEdit / deleteEvent), so memberEmails, notifications and
// Google sync stay in one place.
//
//   values   { title, kind, startDate, endDate, startTime, endTime, notes, location, pods, repeat }
//            repeat = null or { freq: "day"|"week"|"month"|"year", days: [0–6], until } (see repeat.js)
//   onChange(patch)            merge a partial update into values
//   onFill(text)               add mode only: AI fill-in from a sentence typed in the title
//   lockedFromGoogle           edit of a Google twin: title/dates/notes/location come from Google
//   footerStart                node for the footer's left side (the Google sync toggle)
//
// The body keeps the `modal__content` class: index.html blocks touch scrolling inside modals except
// within .modal__content, so on phones the middle of the sheet must carry it to scroll.

const looksLikeSentence = (t) =>
  t.trim().split(/\s+/).length >= 3 ||
  /\b(mon|tue|wed|thu|fri|sat|sun|today|tomorrow|tonight|\d{1,2}\s?(am|pm))\b/i.test(t);

const addDays = (key, n) => {
  const [y, m, d] = key.split("-").map(Number);
  const dt = new Date(y, m - 1, d + n);
  return [dt.getFullYear(), String(dt.getMonth() + 1).padStart(2, "0"), String(dt.getDate()).padStart(2, "0")].join("-");
};
const dateLabel = (key) => (key ? new Date(key + "T00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }) : "Pick a date");

// A native date/time input shown as a pill: the label is what people see; the transparent input on top
// opens the phone's / browser's own picker.
export function PickerField({ type, value, placeholder, label, onChange, min, className = "" }) {
  return (
    <label className={`ef-field ${value ? "" : "ef-field--empty"} ${className}`}>
      <span>{value ? (type === "date" ? dateLabel(value) : formatTime(value)) : placeholder}</span>
      <input
        type={type}
        value={value || ""}
        min={min}
        aria-label={label}
        onChange={(e) => onChange(e.target.value)}
        onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* not allowed in this context */ } }}
      />
    </label>
  );
}

// Times on computers (2026-10-18): Safari and Firefox on a Mac/PC have no dropdown for <input type="time">, so
// the pill above opened nothing there. With a mouse, times are our own typeable field + a list every 15 min
// (the end list shows how long it lasts). Phones and tablets keep the native wheel via PickerField.
const FINE_POINTER = window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;
export const toMin = (t) => { const [h, m] = t.split(":").map(Number); return h * 60 + m; };
export const toKey = (m) => `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
const durLabel = (d) => (d < 60 ? `${d} min` : `${+(d / 60).toFixed(2)} ${d === 60 ? "hr" : "hrs"}`);

// Typed text → "HH:MM", or null when it isn't a time. Reads 7, 730, 7:30, 7.30, 7:30p, 7:30 pm, 7 p.m., 19:30,
// 1930. With no am/pm: after a start time (`after`, minutes) it's the first of AM/PM that comes later;
// otherwise 1–6 mean PM (plans rarely start at 3 AM). The field's result shows right away, so a wrong guess is seen.
export function parseTime(text, after = null) {
  const m = text.trim().toLowerCase().match(/^(\d{1,2})(?:[:.]?(\d{2}))?\s*(?:([ap])\.?\s*(?:m\.?)?)?$/);
  if (!m) return null;
  let h = +m[1];
  const min = m[2] ? +m[2] : 0;
  if (min > 59) return null;
  if (m[3]) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (m[3] === "p" ? 12 : 0);
  } else if (h > 23) {
    return null;
  } else if (h >= 1 && h <= 12 && m[1][0] !== "0") {
    const am = (h % 12) * 60 + min;
    if (after != null) h = am > after || am + 720 <= after ? h % 12 : (h % 12) + 12;
    else if (h <= 6) h += 12;
  }
  return toKey(h * 60 + min);
}

const TIME_CHARS = /[^0-9:.apm\s]/gi; // anything else is dropped as it's typed

export function TimeField({ value, onChange, label, placeholder, after }) {
  const [text, setText] = useState(null); // null = not editing: show the value
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [error, setError] = useState("");
  const listRef = useRef(null);
  const listId = useId();
  const start = after ? toMin(after) : null;
  const options = [];
  for (let m = start != null ? start + 15 : 0; m < 24 * 60; m += 15) {
    options.push({ key: toKey(m), label: formatTime(toKey(m)), dur: start != null ? durLabel(m - start) : "" });
  }
  // Highlight the option nearest the typed time (or the current value) and keep it in view
  const nearest = (key) => (key ? options.findIndex((o) => o.key >= key) : -1);
  useLayoutEffect(() => {
    const list = listRef.current, item = list?.children[active];
    if (item) list.scrollTop = item.offsetTop - list.clientHeight / 2 + item.offsetHeight / 2;
  }, [open, active]);

  const commit = (key) => {
    setText(null); setOpen(false); setError("");
    if (key !== value) onChange(key);
  };
  const finish = () => {
    if (text == null) return setOpen(false);
    if (!text.trim()) return commit("");
    const key = parseTime(text, start);
    if (key) return commit(key);
    setText(null); setOpen(false);
    setError(`“${text.trim()}” isn't a time. Try 7:30 PM.`);
  };
  const onKeyDown = (e) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const i = Math.min(options.length - 1, Math.max(0, active + (e.key === "ArrowDown" ? 1 : -1)));
      setOpen(true); setActive(i); setText(options[i]?.label ?? text);
    } else if (e.key === "Enter") {
      e.preventDefault(); finish();
    } else if (e.key === "Escape" && open) {
      e.preventDefault(); e.stopPropagation(); setText(null); setOpen(false);
    }
  };

  return (
    <div className="ef-time">
      <input
        className={`ef-field ef-field--typed${error ? " ef-field--invalid" : ""}`}
        value={text ?? (value ? formatTime(value) : "")}
        placeholder={placeholder}
        aria-label={label}
        aria-invalid={!!error}
        role="combobox" aria-expanded={open} aria-controls={listId} aria-autocomplete="list"
        maxLength={10} inputMode="text" autoComplete="off" spellCheck={false}
        onFocus={(e) => { setText(value ? formatTime(value) : ""); setOpen(true); setError(""); setActive(nearest(value)); e.target.select(); }}
        onClick={() => setOpen(true)}
        onChange={(e) => {
          const t = e.target.value.replace(TIME_CHARS, "");
          setText(t); setOpen(true);
          const key = parseTime(t, start);
          if (key) setActive(nearest(key));
        }}
        onKeyDown={onKeyDown}
        onBlur={finish}
      />
      {open && options.length > 0 && (
        <div className="ef-timelist" id={listId} role="listbox" aria-label={label} ref={listRef}>
          {options.map((o, i) => (
            <button key={o.key} type="button" role="option" tabIndex={-1} aria-selected={o.key === value}
              className={`ef-timeopt${o.key === value ? " ef-timeopt--sel" : ""}${i === active ? " ef-timeopt--active" : ""}`}
              onMouseDown={(e) => { e.preventDefault(); commit(o.key); }}>
              {o.label}{o.dur && <span>({o.dur})</span>}
            </button>
          ))}
        </div>
      )}
      {error && <div className="ef-timeerr" role="alert">{error}</div>}
    </div>
  );
}

const NativeTime = ({ after, ...p }) => <PickerField type="time" {...p} />;

// The start/end pair used by the event form and the schedule form. Moving the start keeps the length.
export function TimeRange({ startTime, endTime, onChange }) {
  const setStart = (s) => {
    const patch = { startTime: s };
    if (s && startTime && endTime && toMin(endTime) > toMin(startTime)) {
      const end = toMin(s) + toMin(endTime) - toMin(startTime);
      if (end < 24 * 60) patch.endTime = toKey(end);
    }
    onChange(patch);
  };
  const Field = FINE_POINTER ? TimeField : NativeTime;
  return (
    <div className="ef-times">
      <Field label="Start time" placeholder="Start" value={startTime} onChange={setStart} />
      <span className="ef-dash" aria-hidden="true">–</span>
      <Field label="End time" placeholder="End" value={endTime} after={startTime || null} onChange={(e) => onChange({ endTime: e })} />
    </div>
  );
}

const FREQS = [["day", "Day"], ["week", "Week"], ["month", "Month"], ["year", "Year"]];
const WEEKDAYS = [[1, "M", "Monday"], [2, "T", "Tuesday"], [3, "W", "Wednesday"], [4, "T", "Thursday"], [5, "F", "Friday"], [6, "S", "Saturday"], [0, "S", "Sunday"]];

// "Repeats" panel: every day / week (on which days) / month / year, ending never or on a date.
export function RepeatFields({ repeat, startDate, onChange }) {
  const r = repeat;
  const days = r.days?.length ? r.days : [weekdayOf(startDate)];
  return (
    <div className="ef-repeat">
      <div className="ef-repeat__row" role="group" aria-label="Repeats every">
        <span className="ef-repeat__label">Every</span>
        {FREQS.map(([f, label]) => (
          <button key={f} type="button" className="ef-chip" aria-pressed={r.freq === f} onClick={() => onChange({ ...r, freq: f })}>{label}</button>
        ))}
      </div>
      {r.freq === "week" && (
        <div className="ef-repeat__row" role="group" aria-label="On these days">
          <span className="ef-repeat__label">On</span>
          {WEEKDAYS.map(([d, short, long]) => {
            const on = days.includes(d);
            return (
              <button key={d} type="button" className="ef-day" aria-pressed={on} aria-label={long}
                onClick={() => { const next = on ? days.filter((x) => x !== d) : [...days, d]; if (next.length) onChange({ ...r, days: next }); }}>{short}</button>
            );
          })}
        </div>
      )}
      <div className="ef-repeat__row" role="group" aria-label="Ends">
        <span className="ef-repeat__label">Ends</span>
        <button type="button" className="ef-chip" aria-pressed={!r.until} onClick={() => onChange({ ...r, until: null })}>Never</button>
        {r.until ? (
          <PickerField type="date" className="ef-field--date" label="Last date" value={r.until} min={startDate} onChange={(until) => until && onChange({ ...r, until })} />
        ) : (
          <button type="button" className="ef-chip" aria-pressed={false} onClick={() => onChange({ ...r, until: addDays(startDate, 60) })}>On a date</button>
        )}
      </div>
      <div className="ef-hint">{repeatLabel({ ...r, days }, startDate)}{r.until ? "." : ", until you stop it."}</div>
    </div>
  );
}

function TrashIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 7h16M9 7V4h6v3M6 7l1 13h10l1-13" /></svg>;
}

export default function EventForm({
  mode, values, onChange, pods, onSave, onClose, onDelete, onFill, filling = false,
  lockedFromGoogle = false, footerStart = null, autoFocus = false,
}) {
  const v = values;
  const isAdd = mode === "add";
  const away = v.kind === "traveling";
  // UI-only toggles, seeded from the values (the form is keyed per open, so these reset each time)
  const [allDay, setAllDay] = useState(!isAdd && !v.startTime && !v.endTime);
  const [showEnd, setShowEnd] = useState(!!v.endDate && v.endDate !== v.startDate);
  const [showNotes, setShowNotes] = useState(!!v.notes);
  const [showLocation, setShowLocation] = useState(!!v.location);
  const multi = showEnd || away;

  const setKind = (kind) => {
    const patch = { kind };
    if (kind === "traveling") {
      // Trips are usually all-day and span days
      setAllDay(true);
      patch.startTime = "";
      patch.endTime = "";
      if (!v.endDate || v.endDate <= v.startDate) patch.endDate = addDays(v.startDate, 1);
    } else if (away && !showEnd) {
      // Leaving "Away": the end date row disappears, so the event goes back to a single day
      patch.endDate = v.startDate;
    }
    onChange(patch);
  };
  const setStartDate = (startDate) => {
    if (!startDate) return;
    const patch = { startDate };
    if (!multi || !v.endDate || v.endDate < startDate) patch.endDate = startDate;
    onChange(patch);
  };
  const toggleAllDay = () => {
    if (!allDay) onChange({ startTime: "", endTime: "" });
    setAllDay(!allDay);
  };
  const togglePod = (id) => onChange({ pods: v.pods.includes(id) ? v.pods.filter((x) => x !== id) : [...v.pods, id] });

  const hint = v.kind === "busy"
    ? "Pod members see you're unavailable, not what it is."
    : away ? "Pod members see you're away. Great for trips." : "";
  const podList = Object.values(pods);

  return (
    <div className={`ef${isAdd ? " ef--add" : " ef--edit"}`}>
      <div className="ef-head">
        <span className="ef-kicker">{isAdd ? "New event" : "Edit event"}</span>
        <button type="button" className="ef-x" aria-label="Close" onClick={onClose}>✕</button>
      </div>

      <div className="modal__content ef-body">
        {lockedFromGoogle ? (
          <div className="ef-locked">
            <div className="ef-title ef-title--static">{v.title}</div>
            <div className="ef-hint">Title, dates, notes and location sync from Google Calendar.</div>
          </div>
        ) : (
          <div className="ef-titlewrap">
            <input
              className="ef-title"
              placeholder="What's the plan?"
              aria-label="Title"
              value={v.title}
              autoFocus={autoFocus}
              maxLength={300}
              onChange={(e) => onChange({ title: e.target.value })}
              onKeyDown={(e) => { if (e.key === "Enter" && isAdd && looksLikeSentence(v.title)) onFill(v.title); }}
            />
            {isAdd && onFill && looksLikeSentence(v.title) && (
              <button type="button" className="ef-fill" disabled={filling} onClick={() => onFill(v.title)}>
                <span aria-hidden="true">🪄</span>{filling ? "Filling in…" : "AI autofill"}
              </button>
            )}
          </div>
        )}

        <div className="ef-kinds" role="group" aria-label="Type">
          {[["event", "Event"], ["busy", "Busy"], ["traveling", "Away"]].map(([k, label]) => (
            <button key={k} type="button" className={`ef-kind ef-kind--${k}`} aria-pressed={v.kind === k} onClick={() => setKind(k)}>{label}</button>
          ))}
        </div>
        {hint && <div className="ef-hint ef-hint--kind">{hint}</div>}

        {!lockedFromGoogle && (
          <div className="ef-sec">
            <div className="ef-label">When</div>
            <div className="ef-when">
              <PickerField type="date" className="ef-field--date" label={multi ? "Start date" : "Date"} value={v.startDate} onChange={setStartDate} />
              {!allDay && (
                <TimeRange startTime={v.startTime} endTime={v.endTime} onChange={onChange} />
              )}
            </div>
            {multi && (
              <div className="ef-when ef-when--end">
                <span className="ef-ends">Ends</span>
                <PickerField type="date" className="ef-field--date" label="End date" value={v.endDate} min={v.startDate}
                  onChange={(endDate) => endDate && onChange({ endDate })} />
                {!away && (
                  <button type="button" className="ef-link" onClick={() => { setShowEnd(false); onChange({ endDate: v.startDate }); }}>Same day</button>
                )}
              </div>
            )}
            <div className="ef-whenopts">
              <button type="button" className="ef-switch" role="switch" aria-checked={allDay} onClick={toggleAllDay}><i />All day</button>
              <label className="ef-check">
                <input type="checkbox" checked={!!v.repeat} onChange={(e) => onChange({ repeat: e.target.checked ? { freq: "week", days: [weekdayOf(v.startDate)], until: null } : null })} />
                Repeats
              </label>
              {!multi && (
                <button type="button" className="ef-link" onClick={() => { setShowEnd(true); onChange({ endDate: addDays(v.startDate, 1) }); }}>Ends another day</button>
              )}
            </div>
            {v.repeat && <RepeatFields repeat={v.repeat} startDate={v.startDate} onChange={(repeat) => onChange({ repeat })} />}
          </div>
        )}

        <div className="ef-sec">
          <div className="ef-label">Who's it for</div>
          {podList.length === 0 ? (
            <div className="ef-hint">Only you. Create a pod in the Pods tab to share plans.</div>
          ) : (
            <>
              <div className="ef-pods">
                {podList.map((p) => {
                  const c = getPodColor(p.id);
                  const on = v.pods.includes(p.id);
                  return (
                    <button key={p.id} type="button" className="ef-pod" aria-pressed={on} onClick={() => togglePod(p.id)}
                      style={on ? { background: c.bg, color: c.text, borderColor: c.border } : undefined}>
                      <i style={{ background: c.border }} />{p.name}
                    </button>
                  );
                })}
              </div>
              <div className="ef-hint">{v.pods.length ? `Shared with ${v.pods.length === 1 ? "1 pod" : `${v.pods.length} pods`}` : "Only you · pick a pod to share it"}</div>
            </>
          )}
        </div>

        {!lockedFromGoogle && (
          <div className="ef-extras">
            {showLocation && (
              <div className="ef-sec">
                <label className="ef-label" htmlFor="ef-location">Location</label>
                <input id="ef-location" className="form-input" placeholder="Add a place" value={v.location} maxLength={200}
                  autoFocus={!v.location} onChange={(e) => onChange({ location: e.target.value })} />
              </div>
            )}
            {showNotes && (
              <div className="ef-sec">
                <label className="ef-label" htmlFor="ef-notes">Notes</label>
                <textarea id="ef-notes" className="form-textarea" rows={3} placeholder="Anything people should know"
                  value={v.notes} autoFocus={!v.notes} onChange={(e) => onChange({ notes: e.target.value })} />
              </div>
            )}
            {(!showLocation || !showNotes) && (
              <div className="ef-addlinks">
                {!showLocation && <button type="button" className="ef-link" onClick={() => setShowLocation(true)}>+ Location</button>}
                {!showNotes && <button type="button" className="ef-link" onClick={() => setShowNotes(true)}>+ Notes</button>}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ef-foot">
        <div className="ef-foot-start">{footerStart}</div>
        <div className="ef-foot-actions">
          {!isAdd && onDelete && (
            <button type="button" className="ef-trash" aria-label="Delete event" title="Delete event" onClick={onDelete}><TrashIcon /></button>
          )}
          <button type="button" className="btn btn--primary ef-save" disabled={!v.title.trim() || !v.startDate} onClick={onSave}>
            {isAdd ? "Add event" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
